import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Phone, Mail, MessageCircle, Check,
  Flame, ChevronDown, RefreshCw,
} from "lucide-react";
import { API, useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";

/**
 * Today's Priority Queue (Phase 4).
 *
 * Smart-ranked daily call list. Bryan opens his admin in the morning and
 * works top-down. Each row has Call / Text / Email / Mark Complete actions
 * so he never leaves the page. Default 10 rows, expandable to 25/50/100.
 */

const TIER_BADGES = {
  hot:  { label: "HOT",   cls: "bg-red-100 text-red-700 border-red-300" },
  warm: { label: "WARM",  cls: "bg-amber-100 text-amber-700 border-amber-300" },
  cold: { label: "COLD",  cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const QUEUE_SIZES = [10, 25, 50, 100];

const fmtMoney = (lo, hi) => {
  if (!lo && !hi) return "—";
  if (lo && hi)   return `$${lo.toLocaleString()}–$${hi.toLocaleString()}`;
  return `$${(hi || lo).toLocaleString()}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return iso.slice(0, 10); }
};

const relTime = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const PriorityQueue = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/priority-queue?limit=${limit}`, { headers });
      setData(r.data);
    } catch {
      toast({ title: "Failed to load queue", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [headers, limit, toast]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const markComplete = async (leadId) => {
    setCompletingId(leadId);
    try {
      await axios.post(`${API}/admin/priority-queue/${leadId}/complete`, {}, { headers });
      toast({ title: "Marked complete", description: "Stage updated to Contacted." });
      // Refresh the queue so the completed lead drops out
      fetchQueue();
    } catch {
      toast({ title: "Failed to mark complete", variant: "destructive" });
    } finally {
      setCompletingId(null);
    }
  };

  const buildSmsHref = (phone) => `sms:${(phone || "").replace(/\D/g, "")}`;
  const buildCallHref = (phone) => `tel:${(phone || "").replace(/\D/g, "")}`;
  const buildMailHref = (email) => `mailto:${email || ""}`;

  if (loading && data.items.length === 0) {
    return (
      <div className="flex justify-center py-20" data-testid="priority-queue-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="priority-queue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']"
              data-testid="priority-queue-title">
            Today's Priority Queue
          </h1>
          <p className="text-slate-600 text-sm">
            {data.total} actionable leads · ranked by urgency, tier, source &amp; reachability
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500" htmlFor="queue-size">Show:</label>
          <select
            id="queue-size"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            data-testid="queue-size-select"
            className="border-2 border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            {QUEUE_SIZES.map((n) => (
              <option key={n} value={n}>Top {n}</option>
            ))}
          </select>
          <button
            onClick={fetchQueue}
            data-testid="queue-refresh"
            className="p-2 border-2 border-slate-200 rounded-lg hover:bg-slate-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {data.items.length === 0 && !loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="queue-empty">
          <Flame className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">No actionable leads in your queue.</p>
          <p className="text-sm text-slate-400 mt-1">
            Either everything is already in motion, or you need fresh leads. Send a /get-started link!
          </p>
        </div>
      )}

      <ul className="space-y-2" data-testid="priority-queue-list">
        {data.items.map((lead, idx) => {
          const tier = TIER_BADGES[lead.engagement_tier] || TIER_BADGES.cold;
          const isDone = completingId === lead.id;
          return (
            <motion.li
              key={lead.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.02, 0.2) }}
              className="bg-white rounded-2xl border border-slate-200 hover:border-[#DE6E4B] transition overflow-hidden"
              data-testid={`queue-row-${lead.id}`}
            >
              <div className="px-4 sm:px-5 py-4 grid grid-cols-12 gap-3 items-start">
                {/* Rank + tier */}
                <div className="col-span-2 sm:col-span-1 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-slate-300">#{idx + 1}</span>
                  <span className={`mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${tier.cls}`}>
                    {tier.label}
                  </span>
                </div>

                {/* Lead info */}
                <div className="col-span-10 sm:col-span-5 min-w-0">
                  <p className="font-bold text-[#0A2342] truncate" data-testid={`queue-name-${lead.id}`}>
                    {lead.name || "—"}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600">
                    {lead.phone && (
                      <a href={buildCallHref(lead.phone)} className="hover:text-[#DE6E4B]">
                        {lead.phone}
                      </a>
                    )}
                    {lead.email && (
                      <a href={buildMailHref(lead.email)} className="truncate hover:text-[#DE6E4B]" title={lead.email}>
                        {lead.email}
                      </a>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {lead.priority_reasons.map((r) => (
                      <span key={r} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px]">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Meta */}
                <div className="col-span-7 sm:col-span-3 text-xs text-slate-600 space-y-0.5">
                  <p><span className="text-slate-400">Budget</span> · {fmtMoney(lead.budget_min, lead.budget_max)}</p>
                  <p><span className="text-slate-400">Moves</span> · {fmtDate(lead.move_in_date) || lead.move_in_timeline || "—"}</p>
                  <p><span className="text-slate-400">Source</span> · <span className="capitalize">{lead.source || "—"}</span></p>
                  <p><span className="text-slate-400">Last contact</span> · {relTime(lead.last_contacted_at)}</p>
                </div>

                {/* Actions */}
                <div className="col-span-5 sm:col-span-3 flex flex-col sm:items-end gap-1.5">
                  <div className="flex gap-1 w-full sm:w-auto">
                    {lead.phone && (
                      <a
                        href={buildCallHref(lead.phone)}
                        data-testid={`call-${lead.id}`}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700"
                      >
                        <Phone className="w-3 h-3" /> Call
                      </a>
                    )}
                    {lead.phone && (
                      <a
                        href={buildSmsHref(lead.phone)}
                        data-testid={`text-${lead.id}`}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700"
                      >
                        <MessageCircle className="w-3 h-3" /> Text
                      </a>
                    )}
                    {lead.email && (
                      <a
                        href={buildMailHref(lead.email)}
                        data-testid={`email-${lead.id}`}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-slate-700 text-white rounded-lg text-xs font-bold hover:bg-slate-800"
                      >
                        <Mail className="w-3 h-3" /> Email
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => markComplete(lead.id)}
                    disabled={isDone}
                    data-testid={`complete-${lead.id}`}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-3 py-1.5 border-2 border-emerald-500 text-emerald-700 rounded-lg text-xs font-bold hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <Check className="w-3 h-3" /> {isDone ? "Saving…" : "Mark Complete"}
                  </button>
                </div>
              </div>

              {/* Priority score strip */}
              <div className="bg-slate-50 px-4 sm:px-5 py-1.5 border-t border-slate-100 text-[10px] text-slate-500 flex items-center justify-between">
                <span>Priority score: <strong className="text-[#0A2342]">{lead.priority_score}</strong></span>
                <a
                  href={`/admin/leads?focus=${lead.id}`}
                  className="hover:text-[#DE6E4B] inline-flex items-center gap-1"
                >
                  Open full record <ChevronDown className="w-3 h-3 -rotate-90" />
                </a>
              </div>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
};

export default PriorityQueue;
